import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next');
  const qs = searchParams.toString();
  const afterAuthTarget = nextPath
    ? `${nextPath}${searchParams.get('resume') ? (nextPath.includes('?') ? '&' : '?') + 'resume=1' : ''}`
    : '/dashboard';
  const [form, setForm] = useState({ email: localStorage.getItem('kanjusee_remember_email') || '', password: '' });
  const [remember, setRemember] = useState(!!localStorage.getItem('kanjusee_remember_email'));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await authAPI.login({ ...form, role: 'buyer' });
      const user = res.data.data.user;
      if (user.role !== 'buyer') {
        setError('Access denied. This login page is only for buyers.');
        return;
      }
      if (remember) localStorage.setItem('kanjusee_remember_email', form.email);
      else localStorage.removeItem('kanjusee_remember_email');
      login(res.data.data.token, user);
      navigate(afterAuthTarget);
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed.');
    } finally { setLoading(false); }
  };

  return (
    <div className="flex-stack" style={{ minHeight: '100vh', background: 'var(--cream)' }}>
      {/* Left — branding */}
      <div className="hide-mobile" style={{ flex: 1, background: 'var(--green)', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '60px 50px', minWidth: 0 }}>
        <div>
          <span className="logo" style={{ color: '#fff', fontSize: 32 }}>Kanjusee</span>
          <span className="logo-dot" style={{ fontSize: 36 }}>.</span>
        </div>
        <p style={{ fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 700, color: '#fff', marginTop: 24, lineHeight: 1.3 }}>
          India's smartest B2B<br />demand-supply marketplace.
        </p>
        <p style={{ fontSize: 14, color: 'rgba(40, 23, 23, 0.6)', marginTop: 12, lineHeight: 1.7 }}>
          Post demands, get competitive offers from verified vendors, and close deals — all in one platform.
        </p>
        <div style={{ display: 'flex', gap: 20, marginTop: 32 }}>
          {[{ val: '10K+', lbl: 'Demands' }, { val: '5K+', lbl: 'Vendors' }, { val: '98%', lbl: 'Satisfaction' }].map((s, i) => (
            <div key={i}>
              <div style={{ fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 900, color: 'var(--gold)' }}>{s.val}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{s.lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right — form */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 380 }}>
          <div className="show-mobile" style={{ textAlign: 'center', marginBottom: 24 }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <span className="logo" style={{ color: 'var(--green)', fontSize: 28 }}>Kanjusee</span>
              <span className="logo-dot" style={{ fontSize: 32 }}>.</span>
            </Link>
          </div>
          <h2 className="heading-md" style={{ marginBottom: 4 }}>Welcome back, buyer</h2>
          <p className="text-muted text-sm" style={{ marginBottom: 28 }}>Sign in to track your demands and offers. <Link to="/vendor/login" style={{ color: 'var(--green)', fontWeight: 700, textDecoration: 'none' }}>Vendor? Sign in here →</Link></p>

          {error && (
            <div style={{ background: 'var(--red-lt)', color: 'var(--red)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16, fontWeight: 500 }}>{error}</div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-input" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Enter your password" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--muted)', cursor: 'pointer' }}>
                <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: 'var(--green)', cursor: 'pointer' }} />
                Remember me
              </label>
              <Link to="/forgot-password" style={{ fontSize: 12, color: 'var(--green)', cursor: 'pointer', fontWeight: 600, textDecoration: 'none' }}>Forgot password?</Link>
            </div>
            <button type="submit" disabled={loading} className="btn btn-pri btn-lg btn-full">
              {loading ? 'Signing in...' : 'Sign In →'}
            </button>
          </form>

          {nextPath && (
            <div style={{ background: 'var(--gold-lt, #FFF8E7)', color: '#8A5A00', fontSize: 12, padding: '10px 14px', borderRadius: 8, marginBottom: 14 }}>
              We'll bring you right back to your bid after sign in — nothing is lost.
            </div>
          )}

          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--muted)' }}>
            Don't have an account? <Link to={qs ? `/register?${qs}` : '/register'} style={{ color: 'var(--green)', fontWeight: 700, textDecoration: 'none' }}>Create one free</Link>
          </p>
          <p style={{ textAlign: 'center', marginTop: 8 }}>
            <Link to="/" style={{ fontSize: 12, color: 'var(--muted)', textDecoration: 'none' }}>← Back to home</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
